import { AlertTriangle, Calendar, Clock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { differenceInDays, format } from "date-fns";
import { fr } from "date-fns/locale";

export interface DocumentWithExpiration {
  id: string;
  label: string;
  document: {
    expires_at?: string | null;
  };
}

export interface ExpiredDocument {
  id: string;
  label: string;
  expiresAt: Date;
  daysExpired: number;
}

export function getExpiredDocuments(documents: DocumentWithExpiration[]): ExpiredDocument[] {
  const today = new Date();
  return documents
    .filter(doc => doc.document?.expires_at)
    .map(doc => {
      const expiresAt = new Date(doc.document.expires_at!);
      const daysExpired = differenceInDays(today, expiresAt);
      return {
        id: doc.id,
        label: doc.label,
        expiresAt,
        daysExpired,
      };
    })
    .filter(doc => doc.daysExpired > 0)
    .sort((a, b) => b.daysExpired - a.daysExpired);
}

export function getExpiringDocuments(documents: DocumentWithExpiration[], withinDays = 30): ExpiredDocument[] {
  const today = new Date();
  return documents
    .filter(doc => doc.document?.expires_at)
    .map(doc => {
      const expiresAt = new Date(doc.document.expires_at!);
      const daysUntil = differenceInDays(expiresAt, today);
      return {
        id: doc.id,
        label: doc.label,
        expiresAt,
        daysExpired: -daysUntil, // Negative means in the future
      };
    })
    .filter(doc => doc.daysExpired < 0 && doc.daysExpired >= -withinDays)
    .sort((a, b) => a.daysExpired - b.daysExpired);
}

interface ExpirationAlertProps {
  expiredDocs: ExpiredDocument[];
  onContinue: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function ExpirationAlert({ expiredDocs, onContinue, onCancel, loading }: ExpirationAlertProps) {
  if (expiredDocs.length === 0) return null;

  return (
    <Alert variant="destructive" className="mb-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle className="flex items-center gap-2">
        {expiredDocs.length === 1
          ? "1 document expiré"
          : `${expiredDocs.length} documents expirés`}
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <ul className="space-y-1 text-sm">
          {expiredDocs.map(doc => (
            <li key={doc.id} className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              <span className="font-medium">{doc.label}</span>
              <span className="text-muted-foreground">
                (expiré depuis {doc.daysExpired} jour{doc.daysExpired > 1 ? 's' : ''})
              </span>
            </li>
          ))}
        </ul>
        <p className="text-sm">
          Ces documents risquent d'être refusés. Voulez-vous continuer quand même ?
        </p>
      </AlertDescription>
    </Alert>
  );
}

interface ExpirationBadgeProps {
  expiresAt: string | null | undefined;
  compact?: boolean;
}

export function ExpirationBadge({ expiresAt, compact = false }: ExpirationBadgeProps) {
  if (!expiresAt) return null;

  const today = new Date();
  const expDate = new Date(expiresAt);
  const daysUntil = differenceInDays(expDate, today);
  const isExpired = daysUntil < 0;
  const isUrgent = daysUntil >= 0 && daysUntil <= 30;

  if (!isExpired && !isUrgent) return null;

  if (isExpired) {
    return (
      <Badge 
        variant="destructive" 
        className={compact ? "text-[10px] gap-0.5 px-1" : "text-xs gap-1"}
      >
        <AlertTriangle className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
        {compact ? `−${Math.abs(daysUntil)}j` : `Expiré depuis ${Math.abs(daysUntil)}j`}
      </Badge>
    );
  }

  return (
    <Badge 
      variant="outline" 
      className={`${compact ? "text-[10px] gap-0.5 px-1" : "text-xs gap-1"} bg-warning/10 text-warning border-warning/30`}
    >
      <Calendar className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
      {compact ? `${daysUntil}j` : `Expire dans ${daysUntil}j`}
    </Badge>
  );
}
